// ============================================================
// TinyMCE Configuration – JavaScript ENABLED with Security
// ============================================================

// --- Trusted script sources ---
// External domains allowed to load <script> tags (high risk – these run code
// in the page context). Keep this list tight.
const TRUSTED_SCRIPT_DOMAINS = [
  'https://cdn.jsdelivr.net',
  'https://cdnjs.cloudflare.com',
  'https://unpkg.com',
  'https://code.jquery.com',
  'https://stackpath.bootstrapcdn.com',
  'https://cdn.tailwindcss.com',
];

// --- Trusted embed / iframe sources ---
// Domains whose iframes are EXEMPT from sandbox restrictions. Iframes are
// inherently isolated by the browser's same-origin policy, so this list can
// be broader than the script list. Any HTTPS iframe is allowed by CSP (see
// frame-src below), but only these domains skip the extra sandbox attribute.
const TRUSTED_EMBED_DOMAINS = [
  ...TRUSTED_SCRIPT_DOMAINS,
  'https://www.youtube.com',
  'https://www.youtube-nocookie.com',
  'https://player.vimeo.com',
  'https://codepen.io',
  'https://codesandbox.io',
  'https://stackblitz.com',
  'https://replit.com',
  'https://jsfiddle.net',
  'https://trinket.io',
  'https://embed.plnkr.co',
  'https://www.google.com', // Google Forms / Slides embeds
  'https://docs.google.com',
  'https://gist.github.com',
  'https://platform.twitter.com',
  'https://www.instagram.com',
  // Educational tools
  'https://padlet.com',
  'https://kahoot.it',
  'https://create.kahoot.it',
  'https://www.mentimeter.com',
  'https://www.canva.com',
  'https://embed.notion.so',
  'https://miro.com',
  'https://app.miro.com',
  'https://excalidraw.com',
  'https://www.loom.com',
  'https://drive.google.com',
  'https://onedrive.live.com',
  'https://airtable.com',
  'https://app.powerbi.com',
  'https://lookerstudio.google.com',
  'https://slides.com',
  'https://prezi.com',
  'https://www.figma.com',
  'https://genial.ly',
  'https://view.genial.ly',
  'https://h5p.org',
  'https://quizlet.com',
  'https://edpuzzle.com',
  'https://nearpod.com',
  'https://flip.com',
  'https://www.thinglink.com',
  'https://wakelet.com',
];

// Combined list for backward-compat (used by isTrustedUrl for script checks)
const TRUSTED_DOMAINS = [
  ...new Set([...TRUSTED_SCRIPT_DOMAINS, ...TRUSTED_EMBED_DOMAINS]),
];

// --- Dangerous patterns that are ALWAYS blocked ---
// These indicate data-exfiltration or XSS attempts, not legit lesson content.
const DANGEROUS_PATTERNS = [
  /document\.cookie/gi,
  /document\.domain\s*=/gi,
  /window\.location\s*=(?!.*#)/gi, // redirect hijack (anchors OK)
  /localStorage\.(getItem|setItem|removeItem|clear)/gi,
  /sessionStorage\.(getItem|setItem|removeItem|clear)/gi,
  /indexedDB\.open/gi,
  /new\s+XMLHttpRequest/gi,
  /fetch\s*\(\s*(['"`])(?!https:\/\/cdn\.|https:\/\/api\.)/gi, // fetch to non-CDN
  /navigator\.sendBeacon/gi,
  /\.innerHTML\s*=\s*/gi,
  /\.outerHTML\s*=\s*/gi,
  /eval\s*\(/gi,
  /Function\s*\(/gi,
  /setTimeout\s*\(\s*(['"`])/gi, // setTimeout with string arg (eval-like)
  /setInterval\s*\(\s*(['"`])/gi,
  /import\s*\(/gi, // dynamic import()
  /atob\s*\(/gi, // often used to hide payloads
  /\bwith\s*\(/gi,
  /\b__proto__\b/gi,
  /\bconstructor\s*\[/gi,
  /postMessage\s*\(/gi,
];

/**
 * Check whether a URL belongs to the trusted domains whitelist.
 */
const isTrustedUrl = (url) => {
  if (!url) return false;
  const trimmed = url.trim();
  // Relative paths & data URIs are trusted (content is local)
  if (trimmed.startsWith('/') || trimmed.startsWith('data:')) return true;
  return TRUSTED_DOMAINS.some((domain) => trimmed.startsWith(domain));
};

/**
 * Scan HTML string for dangerous patterns. Returns an array of findings.
 */
const scanForThreats = (html) => {
  const findings = [];
  DANGEROUS_PATTERNS.forEach((regex) => {
    const matches = html.match(regex);
    if (matches) {
      findings.push(...matches.map((m) => m.trim().substring(0, 60)));
    }
  });
  return findings;
};

/**
 * Validate all <script src> and <iframe src> against the trusted whitelist.
 * Returns an array of untrusted URLs found.
 */
const findUntrustedSources = (html) => {
  const untrusted = [];
  // Match src="..." on script and iframe tags
  const srcRegex = /<(?:script|iframe)[^>]+src\s*=\s*["']([^"']+)["']/gi;
  let match = srcRegex.exec(html);
  while (match !== null) {
    if (!isTrustedUrl(match[1])) {
      untrusted.push(match[1]);
    }
    match = srcRegex.exec(html);
  }
  return untrusted;
};

/**
 * Strip all <script> blocks from an HTML string (used to sanitise pasted content).
 */
const stripScripts = (html) =>
  html
    .replace(
      /<script[\s\S]*?<\/script>/gi,
      '<!-- [script removed by paste filter] -->'
    )
    .replace(/<script[^>]*\/>/gi, '<!-- [script removed by paste filter] -->');

// --- Build the Content-Security-Policy applied inside TinyMCE's iframe ---
// Scripts are locked to trusted CDN domains only.
// Iframes (frame-src) allow ANY https: source – iframes are already sandboxed
// by the browser's same-origin policy, and admins need to embed many different
// educational tools (Padlet, Kahoot, Mentimeter, Canva, etc.).
const trustedScriptSrcList = TRUSTED_SCRIPT_DOMAINS.join(' ');
const editorCSP = [
  "default-src 'self'",
  `script-src 'self' 'unsafe-inline' 'unsafe-eval' ${trustedScriptSrcList}`,
  `style-src 'self' 'unsafe-inline' https://fonts.googleapis.com ${trustedScriptSrcList}`,
  "img-src 'self' data: blob: https:",
  "media-src 'self' blob: https:",
  "frame-src 'self' https:",
  "font-src 'self' data: https://fonts.gstatic.com https:",
  `connect-src 'self' ${trustedScriptSrcList}`,
  "object-src 'none'",
  "base-uri 'self'",
].join('; ');

// ============================================================
// Main TinyMCE config
// ============================================================
const tinyMCEConfigs = {
  plugins:
    'save preview accordion anchor link autolink autoresize autosave charmap code codesample directionality emoticons fullscreen help image insertdatetime lists advlist media pagebreak quickbars searchreplace table visualblocks visualchars wordcount',
  toolbar:
    'save restoredraft | preview help searchreplace | undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | quickimage quicktable | link image media table code codesample | addcomment showcomments | align lineheight ltr rtl | checklist numlist bullist indent outdent | emoticons charmap | removeformat',
  save_enablewhendirty: true,
  link_default_target: '_blank',
  link_context_toolbar: true,
  autoresize_bottom_margin: 20,
  autoresize_overflow_padding: 10,
  min_height: 400,
  max_height: 900,
  autosave_restore_when_empty: true,

  // --- JavaScript / embedded content support (guarded) ---
  extended_valid_elements:
    'script[src|async|defer|type|charset|crossorigin|integrity|nomodule|referrerpolicy|nonce|id|class],' +
    'style[media|type|scoped|id|class],' +
    'iframe[src|srcdoc|width|height|frameborder|allowfullscreen|sandbox|allow|style|class|id|name|loading|title]',
  valid_children: '+body[script|style],+div[script|style]',
  // Block javascript: protocol in href/src attributes (XSS vector)
  allow_script_urls: false,
  // Do NOT sandbox iframes — embedded apps (Padlet, Kahoot, etc.) need their
  // own scripts to run. Security is enforced via CSP frame-src (https: only)
  // and the browser's same-origin policy, which already isolate iframe content.
  sandbox_iframes: false,
  convert_unsafe_embeds: false,
  // Apply CSP to the editor iframe so even allowed scripts are constrained
  content_security_policy: editorCSP,

  // --- Paste security: strip scripts from pasted content ---
  paste_preprocess: (_editor, pasteArgs) => {
    if (/<script/i.test(pasteArgs.content)) {
      // eslint-disable-next-line no-param-reassign
      pasteArgs.content = stripScripts(pasteArgs.content);
      _editor.notificationManager.open({
        text: '⚠️ Script tags were stripped from pasted content. Use the Code editor (</>) to add scripts manually.',
        type: 'warning',
        timeout: 6000,
      });
    }
  },

  // --- Editor setup: real-time content validation ---
  setup: (editor) => {
    // Intercept content before it's set (e.g. via setContent, paste, undo)
    editor.on('BeforeSetContent', (e) => {
      if (!e.content) return;

      // 1. Block dangerous patterns outright
      const threats = scanForThreats(e.content);
      if (threats.length > 0) {
        const list = threats.slice(0, 5).join('\n • ');
        const extra =
          threats.length > 5 ? `\n … and ${threats.length - 5} more` : '';
        const msg =
          '⚠️ SECURITY WARNING\n\n' +
          "The content you're inserting contains potentially dangerous code:\n\n" +
          ` • ${list}${extra}\n\n` +
          "These patterns can steal data or compromise students' browsers.\n\n" +
          'Click OK to insert anyway (at your own risk), or Cancel to block it.';
        const proceed = window.confirm(msg);
        if (!proceed) {
          e.preventDefault();
        }
      }

      // 2. Warn about untrusted external script/iframe sources
      const untrusted = findUntrustedSources(e.content);
      if (untrusted.length > 0) {
        const urls = untrusted.slice(0, 5).join('\n • ');
        const extra =
          untrusted.length > 5 ? `\n … and ${untrusted.length - 5} more` : '';
        const msg =
          '⚠️ UNTRUSTED SOURCES\n\n' +
          'The following external URLs are not in the trusted whitelist:\n\n' +
          ` • ${urls}${extra}\n\n` +
          'Only load scripts/iframes from sources you trust.\n\n' +
          'Click OK to allow, or Cancel to block.';
        const proceed = window.confirm(msg);
        if (!proceed) {
          e.preventDefault();
        }
      }
    });

    // Visual indicator when content has active scripts
    editor.on('NodeChange', () => {
      const doc = editor.getDoc();
      if (!doc) return;
      const scripts = doc.querySelectorAll('script');
      const bar = editor.getContainer()?.querySelector('.tce-script-badge');
      if (scripts.length > 0) {
        if (!bar) {
          const badge = document.createElement('div');
          badge.className = 'tce-script-badge';
          badge.style.cssText =
            'background:#fef3c7;color:#92400e;font-size:12px;padding:4px 12px;' +
            'border-bottom:1px solid #fcd34d;display:flex;align-items:center;gap:6px;';
          badge.innerHTML =
            '🛡️ <strong>This content contains &lt;script&gt; tags</strong> — they will execute for students. Ensure all sources are trusted.';
          const container = editor.getContainer();
          container?.insertBefore(badge, container.firstChild);
        }
      } else if (bar) {
        bar.remove();
      }
    });
  },

  codesample_languages: [
    { text: 'HTML/XML', value: 'markup' },
    { text: 'JavaScript', value: 'javascript' },
    { text: 'CSS', value: 'css' },
    { text: 'PHP', value: 'php' },
    { text: 'Ruby', value: 'ruby' },
    { text: 'Python', value: 'python' },
    { text: 'Java', value: 'java' },
    { text: 'C', value: 'c' },
    { text: 'C#', value: 'csharp' },
    { text: 'C++', value: 'cpp' },
    { text: 'Swift', value: 'swift' },
    { text: 'Kotlin', value: 'kotlin' },
    { text: 'TypeScript', value: 'typescript' },
    { text: 'SQL', value: 'sql' },
    { text: 'Go', value: 'go' },
    { text: 'Rust', value: 'rust' },
    { text: 'R', value: 'r' },
    { text: 'MATLAB', value: 'matlab' },
    { text: 'Shell', value: 'shell' },
    { text: 'Dart', value: 'dart' },
    { text: 'Haskell', value: 'haskell' },
    { text: 'Perl', value: 'perl' },
    { text: 'Scala', value: 'scala' },
  ],
  image_caption: true,
  image_advtab: true,
  images_file_types: 'svg jpeg,jpg,jpe,jfi,jif,jfif,png,gif,bmp,webp',
  a11y_advanced_options: true,
  file_picker_types: 'file image media',
  quickbars_insert_toolbar: 'quickimage quicktable | hr pagebreak',
  quickbars_image_toolbar:
    'alignleft aligncenter alignright | rotateleft rotateright | imageoptions',
};

export {
  TRUSTED_SCRIPT_DOMAINS,
  TRUSTED_EMBED_DOMAINS,
  TRUSTED_DOMAINS,
  isTrustedUrl,
  scanForThreats,
  stripScripts,
};
export default tinyMCEConfigs;
